import React, { useState, useEffect } from 'react';
import { Database, Table2, RefreshCw, ChevronRight, Eye, Lock, FileCode } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface CollectionInfo {
  name: string;
  description?: string;
  document_count: number;
  has_rules: boolean;
  last_modified: string;
  usage: {
    components: string[];
    hooks: string[];
    pages: string[];
  };
}

// Lista de coleções conhecidas do sistema
const KNOWN_COLLECTIONS = [
  'students',
  'metrics',
  'email_engagements'
];

// Mapeamento de regras de segurança conhecidas
const KNOWN_RULES = {
  'students': true,
  'metrics': true,
  'email_engagements': true
};

// Mapeamento de uso das coleções no sistema
const COLLECTION_USAGE = {
  'students': {
    components: [
      'StudentList',
      'StudentProfileModal'
    ],
    hooks: [
      'useStudents',
      'useTopStudents'
    ],
    pages: [
      'Dashboard',
      'StudentsPage'
    ]
  },
  'metrics': {
    components: [
      'MetricCard',
      'DetailedBarChart'
    ],
    hooks: [
      'useMetrics',
      'useDashboardMetrics'
    ],
    pages: [
      'Dashboard',
      'OpenRateReport'
    ]
  },
  'email_engagements': {
    components: [
      'LineChart',
      'GroupedBarChart'
    ],
    hooks: [
      'useEmailOpens',
      'useRealtimeData'
    ],
    pages: [
      'EmailOpensPage',
      'OpenRateReport'
    ]
  }
};

export function FirebaseTables() {
  const [collections, setCollections] = useState<CollectionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);

  const getCollectionDescription = (collectionName: string): string => {
    const descriptions: Record<string, string> = {
      'students': 'Dados dos alunos e métricas de engajamento',
      'metrics': 'Métricas gerais do sistema',
      'email_engagements': 'Registros de interações com e-mails'
    };

    return descriptions[collectionName] || 'Coleção do sistema';
  };

  const fetchCollectionInfo = async (collectionName: string): Promise<CollectionInfo | null> => {
    try {
      const collectionRef = collection(db, collectionName);
      const snapshot = await getDocs(collectionRef);

      return {
        name: collectionName,
        description: getCollectionDescription(collectionName),
        document_count: snapshot.size,
        has_rules: KNOWN_RULES[collectionName] || false,
        last_modified: new Date().toISOString(),
        usage: COLLECTION_USAGE[collectionName] || { components: [], hooks: [], pages: [] }
      };
    } catch (err) {
      console.error(`Erro ao buscar informações da coleção ${collectionName}:`, err);
      return null;
    }
  };

  const fetchCollections = async () => {
    try {
      setLoading(true);
      setError(null);

      const collectionsInfo = await Promise.all(
        KNOWN_COLLECTIONS.map(fetchCollectionInfo)
      );

      setCollections(collectionsInfo.filter(Boolean) as CollectionInfo[]);
    } catch (err) {
      console.error('Erro ao buscar coleções:', err);
      setError('Erro ao carregar informações das coleções');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-[#2C2D3C] p-6 rounded-lg">
            <div className="h-6 bg-[#3C3D4C] rounded w-1/4"></div>
            <div className="h-4 bg-[#3C3D4C] rounded w-3/4 mt-2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
        <p>{error}</p>
        <button
          onClick={fetchCollections}
          className="mt-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#3C3D4C] rounded-lg">
            <Database className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Coleções do Firebase</h3>
            <p className="text-sm text-gray-400">Estrutura e status das coleções</p>
          </div>
        </div>
        <button
          onClick={fetchCollections}
          className="p-2 bg-[#2C2D3C] text-gray-300 rounded-lg hover:bg-[#3C3D4C] transition-colors"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-3">
        {collections.map((collection) => (
          <div
            key={collection.name}
            className="bg-[#2C2D3C] p-4 rounded-lg hover:bg-[#3C3D4C] transition-colors cursor-pointer"
            onClick={() => setSelectedCollection(selectedCollection === collection.name ? null : collection.name)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#1C1D2C] rounded-lg">
                  <Table2 className="w-4 h-4 text-orange-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">{collection.name}</h4>
                  <p className="text-sm text-gray-400">{collection.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  {collection.has_rules && (
                    <div className="flex items-center gap-1 text-emerald-400">
                      <Lock className="w-4 h-4" />
                      <span className="text-xs">Regras</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-gray-400">
                    <Eye className="w-4 h-4" />
                    <span className="text-xs">{collection.document_count} documentos</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-400">
                    <FileCode className="w-4 h-4" />
                    <span className="text-xs">
                      {collection.usage.components.length + collection.usage.hooks.length + collection.usage.pages.length} referências
                    </span>
                  </div>
                </div>
                <ChevronRight 
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    selectedCollection === collection.name ? 'rotate-90' : ''
                  }`} 
                />
              </div>
            </div>

            {selectedCollection === collection.name && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-400">Total de Documentos</p>
                    <p className="text-lg font-medium text-white mt-1">
                      {collection.document_count.toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Regras de Segurança</p>
                    <p className="text-lg font-medium text-white mt-1">
                      {collection.has_rules ? 'Ativadas' : 'Desativadas'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Última Modificação</p>
                    <p className="text-lg font-medium text-white mt-1">
                      {new Date(collection.last_modified).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>

                {/* Uso da coleção no sistema */}
                <div className="mt-6 space-y-4">
                  <h5 className="text-sm font-medium text-gray-400">Uso no Sistema</h5>
                  
                  {/* Componentes */}
                  {collection.usage.components.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Componentes</p>
                      <div className="flex flex-wrap gap-2">
                        {collection.usage.components.map(component => (
                          <span key={component} className="px-2 py-1 bg-orange-500/10 text-orange-400 rounded text-xs">
                            {component}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hooks */}
                  {collection.usage.hooks.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Hooks</p>
                      <div className="flex flex-wrap gap-2">
                        {collection.usage.hooks.map(hook => (
                          <span key={hook} className="px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs">
                            {hook}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Páginas */}
                  {collection.usage.pages.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Páginas</p>
                      <div className="flex flex-wrap gap-2">
                        {collection.usage.pages.map(page => (
                          <span key={page} className="px-2 py-1 bg-green-500/10 text-green-400 rounded text-xs">
                            {page}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}